/**
 * Admin: Partner Resource candidates queue.
 *
 * Driven by the weekly Sunday 06:30 scraper (`partner_resource_scraper.py`)
 * that sweeps VisitEngland / VisitBritain index pages for new toolkits and
 * guidance. Each candidate row has:
 *   - the proposed link + anchor text
 *   - the section the scraper suggests (admin can override)
 *   - approve → bullet is appended to /partners/advice-guidance and the
 *     candidate is marked approved (and won't be re-proposed)
 *   - reject → recorded with optional reason, also de-dupes future runs
 */
import React, { useEffect, useState } from 'react';
import { Check, ExternalLink, PlayCircle, RefreshCw, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

const SECTIONS = [
  { value: 'accessibility', label: 'Accessibility & inclusion' },
  { value: 'regenerative', label: 'Regenerative & sustainable tourism' },
  { value: 'data', label: 'Data, insights & visitor trends' },
  { value: 'marketing', label: 'Marketing & business growth' },
  { value: 'funding', label: 'Funding, grants & emergency response' },
];

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function AdminPartnerResources() {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  async function load() {
    setErr('');
    try {
      const q = filter ? `?status=${filter}` : '';
      const { data } = await api.get(`/admin/partner-resources${q}`);
      setItems(data.items || []);
      setRuns(data.recent_runs || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load candidates');
    }
  }
  useEffect(() => { load(); }, [filter]);

  async function runScrape() {
    setBusy('scrape'); setErr('');
    try {
      const { data } = await api.post('/admin/partner-resources/scrape', {});
      await load();
      alert(`Scrape complete — ${data.candidates_created} new candidate${data.candidates_created === 1 ? '' : 's'}, ${data.pages_fetched} index pages fetched.`);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Scrape failed');
    } finally { setBusy(''); }
  }

  async function approve(id, section, label) {
    setBusy(`approve-${id}`); setErr('');
    try {
      await api.post(`/admin/partner-resources/${id}/approve`, { section, label });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Approve failed');
    } finally { setBusy(''); }
  }

  async function reject(id) {
    const reason = window.prompt('Optional rejection reason:', '');
    if (reason === null) return;
    setBusy(`reject-${id}`); setErr('');
    try {
      await api.post(`/admin/partner-resources/${id}/reject`, { reason });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Reject failed');
    } finally { setBusy(''); }
  }

  return (
    <div className="space-y-6" data-testid="admin-partner-resources">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl mb-1">Partner Resource Candidates</h1>
          <p className="text-sm text-charcoal/65">Weekly sweep of VisitEngland &amp; VisitBritain. Approve to publish on the Advice &amp; Support hub.</p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                filter === s
                  ? 'bg-heritage text-base border-heritage'
                  : 'border-charcoal/15 text-charcoal/70 hover:border-charcoal/30'
              }`}
            >
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button
            type="button"
            onClick={runScrape}
            disabled={busy === 'scrape'}
            data-testid="run-scrape-now"
            className="inline-flex items-center gap-1.5 bg-charcoal text-base text-sm font-semibold rounded-full px-4 py-2 hover:bg-heritage disabled:opacity-50"
            title="Manually trigger the weekly scrape now"
          >
            {busy === 'scrape' ? <RefreshCw size={14} className="animate-spin" /> : <PlayCircle size={14} />}
            {busy === 'scrape' ? 'Scraping…' : 'Run scrape now'}
          </button>
        </div>
      </header>

      <HelpCallout>
        The scrape runs automatically every Sunday at 06:30. Approved links are appended to the
        relevant section of <code>/partners/advice-guidance</code> with today&apos;s date. Rejected links
        won&apos;t be re-proposed.
      </HelpCallout>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{err}</div>}

      {runs.length > 0 && (
        <details className="bg-stone/40 border border-charcoal/10 rounded-2xl p-4 text-sm" data-testid="recent-runs">
          <summary className="cursor-pointer text-charcoal/65 font-semibold">Recent scrape runs ({runs.length})</summary>
          <ul className="mt-3 space-y-1">
            {runs.map((r, i) => (
              <li key={i} className="text-xs text-charcoal/65">
                {fmt(r.started_at)} — {r.pages_fetched} pages · {r.candidates_created} new · {r.skipped_duplicate} dup · {r.skipped_in_page} already-in-page
                {r.errors && r.errors.length > 0 && <span className="text-red-700"> · {r.errors.length} error(s)</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {items.length === 0 ? (
        <div className="text-sm text-charcoal/55 text-center py-16 border border-dashed border-charcoal/15 rounded-2xl" data-testid="empty-state">
          No {filter} candidates. {filter === 'pending' && 'Click "Run scrape now" to fetch a fresh batch.'}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="candidates-list">
          {items.map((c) => (
            <CandidateRow key={c.id} c={c} busy={busy} onApprove={approve} onReject={reject} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CandidateRow({ c, busy, onApprove, onReject }) {
  const [section, setSection] = useState(c.suggested_section);
  const [label, setLabel] = useState(c.anchor_text);
  const isPending = c.status === 'pending';

  return (
    <li className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`candidate-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <a href={c.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-heritage hover:underline truncate" data-testid={`url-${c.id}`}>
              {c.anchor_text}
            </a>
            <ExternalLink size={12} className="text-charcoal/40" />
          </div>
          <div className="text-xs text-charcoal/55 truncate">{c.url}</div>
          <div className="text-xs text-charcoal/55 mt-1">
            From <strong>{c.source_name}</strong> · seen {fmt(c.created_at)}
            {c.status !== 'pending' && (
              <span> · <strong>{c.status}</strong>{c.reviewed_at ? ` ${fmt(c.reviewed_at)}` : ''}{c.reviewed_by ? ` by ${c.reviewed_by}` : ''}</span>
            )}
          </div>
        </div>
        {isPending ? (
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-end shrink-0">
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              data-testid={`section-${c.id}`}
              className="text-xs border border-charcoal/15 rounded-lg px-2 py-1.5 bg-white"
            >
              {SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid={`label-${c.id}`}
              className="text-xs border border-charcoal/15 rounded-lg px-2 py-1.5 bg-white w-48"
              title="Markdown link label"
            />
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => onApprove(c.id, section, label)}
                disabled={busy === `approve-${c.id}`}
                data-testid={`approve-${c.id}`}
                className="inline-flex items-center gap-1.5 bg-heritage text-base text-sm font-semibold rounded-full px-4 py-2 hover:bg-charcoal disabled:opacity-50"
              >
                <Check size={14} /> Approve
              </button>
              <button
                type="button"
                onClick={() => onReject(c.id)}
                disabled={busy === `reject-${c.id}`}
                data-testid={`reject-${c.id}`}
                className="inline-flex items-center gap-1.5 border border-charcoal/20 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone disabled:opacity-50"
              >
                <X size={14} /> Reject
              </button>
            </div>
          </div>
        ) : (
          <div className="text-xs text-charcoal/55 shrink-0">
            {c.status === 'approved' && c.approved_section && <>→ {SECTIONS.find((s) => s.value === c.approved_section)?.label || c.approved_section}</>}
            {c.status === 'rejected' && c.rejection_reason && <em>{c.rejection_reason}</em>}
          </div>
        )}
      </div>
    </li>
  );
}
